import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { ReactComponent as Guy } from "../../assets/svg/guy.svg";
import { MainStore } from "../../stores/Main";
import FreedeLogo from "../../assets/svg/logo-dark.svg";
import TestLogo from "../../assets/svg/logo-testagency.svg";
import TestBLogo from "../../assets/svg/logo-testagencyb.svg";
import KFLogo from "../../assets/svg/logo-knutefinancial.svg";
import PoweredByImg from "../../assets/svg/powered-by.svg";
import PoweredBy from "../../utils/poweredBy";
import DebtDisclosure from "../../components/debt-disclosure/debt-disclosure";
import DisclosureModal from "../../components/disclosure-modal/disclosure-modal";
import CloudsImg from "../../assets/images/clouds.png";

const LayoutAuth = (props) => {
  const mainStore = useContext(MainStore);
  const agencyDetails = mainStore.agencyDetails || {};
  const showIllustration = props.showIllustration !== false; // Default to true if not specified
  const showContainer = props.showContainer !== false; // Default to true if not specified
  const [showDisclosureModal, setShowDisclosureModal] = React.useState(false);

  // Dynamic logo selection based on hostname
  let logoPath = FreedeLogo;
  let logoHeight = "41";
  let logoWidth = "159";

  if (window.location.host.includes("testagencyb")) {
    logoPath = TestBLogo;
  } else if (
    window.location.host.includes("testagency") ||
    window.location.host.includes("freede-agency")
  ) {
    logoPath = TestLogo;
  } else if (window.location.host.includes("knute")) {
    logoPath = KFLogo;
    logoHeight = "32";
    logoWidth = "120";
  }

  return (
    // w-full: the questionnaire pages render this inside the legacy `.main`
    // (display:flex) wrapper, where as a flex item it shrinks to content
    // width and the page gradient stops mid-screen (black band on the right).
    <div className="w-full min-h-screen h-full text-white bg-gradient-to-b from-[#071427] via-[#071427] to-[#06111f] relative overflow-hidden flex flex-col">
      {/* Background orbs */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-blue-500/30 blur-3xl"
      />

      {/* Cloud background image - faded */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `url(${CloudsImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Header - consistent with landing page */}
      <header className="relative z-10">
        <div className="mx-auto max-w-6xl px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 select-none"
            aria-label={agencyDetails.name || "Home"}
          >
            <img
              src={logoPath}
              alt={agencyDetails.name || "Home"}
              height={logoHeight}
              width={logoWidth}
              className="drop-shadow-sm"
              draggable={false}
            />
          </Link>

          {/* Trust indicator */}
          <div className="flex items-center gap-2 text-xs text-white/60">
            <ShieldCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Secure & encrypted</span>
          </div>
        </div>
      </header>

      {/* Main container with max-width */}
      <div className="relative z-10 flex-1">
        <div className="mx-auto max-w-6xl px-0 sm:px-6">
          <div
            className={`grid grid-cols-1 ${showIllustration ? "lg:grid-cols-2" : ""} gap-10 py-2 sm:py-8 md:py-12`}
          >
            {/* Left side - Form content */}
            <div className="flex flex-col justify-center">
              {/* Form container with glassmorphism effect */}
              <div
                className={`w-full ${showIllustration ? "max-w-md mx-auto lg:mx-0" : "max-w-4xl mx-auto"}`}
              >
                {showContainer ? (
                  <div className="rounded-3xl border border-white/10 bg-white/[.06] p-8 shadow-2xl backdrop-blur-sm">
                    {props.children}
                  </div>
                ) : (
                  props.children
                )}
              </div>
            </div>

            {/* Right side - Illustration (hidden on mobile and when showIllustration is false) */}
            {showIllustration && (
              <div className="hidden lg:flex items-center justify-center">
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute -inset-16 rounded-full bg-blue-400/20 blur-3xl" />

                  <motion.div
                    initial={{ y: 6, rotate: 0 }}
                    animate={{ y: [6, -6, 6] }}
                    transition={{
                      duration: 5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="relative"
                  >
                    <Guy className="w-[400px] h-auto drop-shadow-xl select-none" />
                  </motion.div>

                  {/* Text below illustration */}
                  <div className="mt-8 text-center max-w-sm mx-auto">
                    <h2 className="text-lg font-bold mb-1 text-white">
                      A new way to manage debt on your terms.
                    </h2>
                    <p className="text-white/75 text-sm">
                      Freede from worry. Freede from stress. Freede from debt.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer - consistent with landing page */}
      <footer className="relative z-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col gap-3 sm:gap-4">
              {/* Agency info - stacked on mobile */}
              <div className="text-center text-white/70 text-xs sm:text-sm">
                {agencyDetails.name && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-3">
                    <span className="font-medium">{agencyDetails.name}</span>
                    <span className="hidden sm:inline text-white/40">•</span>
                    <span>{agencyDetails.phone}</span>
                    <div className="sm:hidden" />
                    <span className="hidden sm:inline text-white/40">•</span>
                    <span className="text-xs">
                      {agencyDetails.address}, {agencyDetails.city} {agencyDetails.state} {agencyDetails.zip}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Powered by - centered */}
              <div className="flex justify-center">
                {PoweredBy && (
                  <img
                    src={PoweredByImg}
                    alt="Powered by Freede"
                    className="h-4 sm:h-5 opacity-80 hover:opacity-100 transition-opacity"
                  />
                )}
              </div>
            </div>
            
            {/* Links and copyright */}
            <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2 sm:gap-4 text-xs text-white/60">
              <div className="flex items-center justify-center gap-3 sm:gap-4">
                <a
                  className="hover:text-white"
                  href="https://www.freede.co/legal-terms-platform"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Use
                </a>
                <a
                  className="hover:text-white"
                  href="https://www.freede.co/legal-privacy-platform"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                {mainStore.isAuthenticated ? (
                  <Link
                    to="/dashboard/disclosures"
                    className="hover:text-white"
                  >
                    Consumer Rights & Disclosures
                  </Link>
                ) : (
                  <button
                    onClick={() => setShowDisclosureModal(true)}
                    className="hover:text-white"
                  >
                    Consumer Rights & Disclosures
                  </button>
                )}
              </div>
              <div className="text-center text-xs">
                © {new Date().getFullYear()} Freede Solutions Inc. All rights
                reserved.
              </div>
            </div>
            <DebtDisclosure agencyDetails={agencyDetails} />
            {agencyDetails?.extra_footer && (
              <div 
                className="mt-3 text-center text-xs text-white/40 max-w-3xl mx-auto px-4"
                dangerouslySetInnerHTML={{ __html: agencyDetails.extra_footer }}
              />
            )}
          </div>
        </div>
      </footer>
      <DisclosureModal 
        isOpen={showDisclosureModal} 
        onClose={() => setShowDisclosureModal(false)} 
      />
    </div>
  );
};

export default LayoutAuth;
